import {
  Pile,
  Proposition,
  Validation,
  findAndMap,
  isJsonEqual,
  sortBy,
  sortByMultiple,
} from "~/utils";
import { Choice } from "./Choice";
import {
  EffectExecution,
  EffectExecutionManager,
} from "./EffectExecutionManager2";
import { GameInfoEvent, gameEventBuilder } from "./GameEvent";
import { InfiniteCardPile } from "./InfiniteCardPile";
import { Player } from "./Player";
import { Podium } from "./Podium";
import { TechTile } from "./TechTile";
import { ActionSlot } from "./actionSlot/ActionSlot";
import {
  BattleCard,
  battleCardsDefRepository,
} from "./battleCards/BattleCardProps";
import { Card } from "./card/Card";
import { cardsDefRepository } from "./card/cardsDefRepository";
import {
  AgentIcon,
  FactionRampBonus,
  FactionType,
  Meaple,
  ResourceType,
  TurnPhase,
  agentIconToFactionType,
} from "./constant";
import { AgentEffectArgs, EffectChoiceResult } from "./effect";
import {
  AgentEffectCommands,
  AgentEffectDescription,
  AgentEffectFunctions,
  getEffectArg,
  isAgentEffectEqual,
} from "./effect/AgentEffect";
import {
  IntrigueCard,
  intrigueCardsDefRepository,
} from "./intrigueCards/IntrigueCardProps";

export type GameProps = {
  players: Player[];
  currentPlayerIndex: number;
  firstPlayerIndex: number;
  actionSlots: ActionSlot[];
  shopDeck: Pile<Card>;
  shop: Pile<Card>;
  TSMFPile: InfiniteCardPile;
  ArrakisLiaisonPile: InfiniteCardPile;
  battleDeck: Pile<BattleCard>;
  battleDiscard: Pile<BattleCard>;
  turnPhase: TurnPhase;
  mentatOnSlotOpt?: Meaple;
  intrigueDeck: Pile<IntrigueCard>;
  intrigueDiscard: Pile<IntrigueCard>;
  effectExecution: EffectExecution;
  skipCleanMentat: boolean;
  infoEvents: GameInfoEvent[];
  techTiles: Pile<TechTile>[];
  trashedTechTiles: Pile<TechTile>;
  startTurnSlotWithDreadnought: string[];
};

export class Game implements GameProps {
  players: Player[];
  currentPlayerIndex: number;
  firstPlayerIndex: number;
  actionSlots: ActionSlot[];
  shopDeck: Pile<Card>;
  shop: Pile<Card>;
  TSMFPile: InfiniteCardPile;
  ArrakisLiaisonPile: InfiniteCardPile;
  battleDeck: Pile<BattleCard>;
  battleDiscard: Pile<BattleCard>;
  turnPhase: TurnPhase;
  mentatOnSlotOpt?: Meaple;
  intrigueDeck: Pile<IntrigueCard>;
  intrigueDiscard: Pile<IntrigueCard>;
  effectExecution: EffectExecution;
  skipCleanMentat: boolean;
  infoEvents: GameInfoEvent[];
  techTiles: Pile<TechTile>[];
  trashedTechTiles: Pile<TechTile>;
  startTurnSlotWithDreadnought: string[];

  constructor(props: GameProps) {
    Object.assign(this, props);
  }
  get currentBattleCard(): BattleCard {
    const lastBattleCard = this.battleDiscard.last();
    if (!lastBattleCard) {
      throw new Error("unexpected: no battle card");
    }
    return lastBattleCard;
  }
  get currentPlayer(): Player {
    return this.players[this.currentPlayerIndex];
  }
  get fightingPlayers(): Player[] {
    return this.players.filter(
      (player) => player.combatTroops.length || player.combatDreadnought.length
    );
  }
  getOpponents(playerId: string): Player[] {
    return this.players.filter((player) => player.id !== playerId);
  }
  validateCurrentPlayer(playerId: string): Validation {
    if (this.currentPlayer.id !== playerId) {
      return "this is not your turn";
    }
  }
  validateActionSlotPlay(playerId: string, actionSlotId: string): Validation {
    const playerIdValidation = this.validateCurrentPlayer(playerId);
    if (playerIdValidation) return playerIdValidation;
    const player = this.getPlayer(playerId);
    const { hand } = player;
    if (!hand.length) {
      return "hand is empty";
    }
    const errorOpts = hand.pile.flatMap((card) =>
      this.validatePlay(playerId, card.id, actionSlotId)
    );
    if (errorOpts.every((errorOpt) => errorOpt)) {
      return errorOpts[0];
    }
  }
  validatePlay(
    playerId: string,
    cardId: string,
    actionSlotId: string
  ): Validation {
    const playerIdValidation = this.validateCurrentPlayer(playerId);
    if (playerIdValidation) return playerIdValidation;
    const noEffectValidation = this.validateNoEffect();
    if (noEffectValidation) return noEffectValidation;

    const actionSlot = this.getActionSlot(actionSlotId);
    const { currentPlayer: player } = this;
    const card = player.hand.findById(cardId);
    if (!card) {
      return "could not find card in hand";
    }
    const {
      resources,
      workerPile,
      ignoreOccupiedAgentSlots,
      ignoreAgentIcons,
      playerLeader: { globalEffect },
      hasExtraMeaple,
      hasSiege,
    } = player;
    if (
      (actionSlot.id === "Swordmaster" && hasExtraMeaple) ||
      (actionSlot.id === "High Council" && hasSiege)
    ) {
      return "this slot can only be played once per game";
    }
    if (
      actionSlot.id === "Sietch Tabr" &&
      player.getFactionAffinity(FactionType.Fremen).level < 2
    ) {
      return "you need Fremen affinity level 2 to play on this slot";
    }
    if (
      actionSlot.id === "Interstellar Shipping" &&
      player.getFactionAffinity(FactionType.Guild).level < 2
    ) {
      return "you need Guild affinity level 2 to play on this slot";
    }
    if (
      actionSlot.id === "Sell melange" &&
      player.getResource(ResourceType.Spice) < 2
    ) {
      return "not enough spice to trade";
    }
    const {
      agentIcons: cardAgentIcons,
      playEffects,
      hasInfiltration,
    } = cardsDefRepository.getDef(card);
    const { agentIcon: slotAgentIcon, meaples } = actionSlot;
    const cost = this.getActionSlotCost(actionSlotId, cardId);

    if (workerPile.isEmpty()) {
      return "you don't have worker";
    }
    if (
      !ignoreAgentIcons &&
      !cardAgentIcons.find((cardAgentIcon) => slotAgentIcon === cardAgentIcon)
    ) {
      return `could not find agentIcon ${slotAgentIcon}`;
    }
    if (cost && !resources.canPay(cost)) {
      return `cannot pay ${cost.toString()}`;
    }
    const isPlayAnywhere = playEffects.some((effect) =>
      isAgentEffectEqual(effect, AgentEffectCommands.playAnywhere)
    );
    if (
      !isPlayAnywhere &&
      !meaples.isEmpty() &&
      !ignoreOccupiedAgentSlots &&
      !(
        hasInfiltration &&
        meaples.pile.every((meaple) => meaple.owner !== playerId)
      ) &&
      !(
        AgentEffectDescription.is(globalEffect, "eyesEverywhere") &&
        [AgentIcon.City, AgentIcon.Landsraad].includes(slotAgentIcon) &&
        meaples.pile.every((meaple) => meaple.owner !== playerId)
      )
    ) {
      return "action slot already occupied";
    }
    const { blockedBy = [] } = actionSlot;
    if (blockedBy.some((id) => player.id !== id)) {
      return "action slot is blocked by another player";
    }
  }
  private getActionSlotCost(actionSlotId: string, cardId: string) {
    const actionSlot = this.getActionSlot(actionSlotId);
    const card = this.currentPlayer.getCard(cardId);
    const cardDef = card.getDef(cardsDefRepository);
    const {
      currentPlayer: {
        playerLeader: { globalEffect },
      },
    } = this;
    const { cost, agentIcon: slotAgentIcon } = actionSlot;
    const firstPlayEffect = cardDef.playEffects.at(0);
    if (
      cost &&
      cost.getResource(ResourceType.Solaris)?.value &&
      slotAgentIcon === AgentIcon.Landsraad &&
      AgentEffectDescription.is(globalEffect, "landsraadPopularity")
    ) {
      const newCost = cost.copy();
      newCost.pay({
        [ResourceType.Solaris]: 1,
      });
      return newCost;
    } else if (
      cost &&
      actionSlot.id === "Heighliner" &&
      firstPlayEffect &&
      AgentEffectDescription.is(firstPlayEffect, "heighlinerDiscount") &&
      Proposition.isValueProposition(firstPlayEffect)
    ) {
      const discount = getEffectArg<"heighlinerDiscount">(firstPlayEffect);
      const newCost = cost.copy();
      newCost.pay({
        [ResourceType.Spice]: discount,
      });
      return newCost;
    } else {
      return cost;
    }
  }
  play(playerId: string, cardId: string, actionSlotId: string) {
    Validation.validate(this.validatePlay(playerId, cardId, actionSlotId));
    const actionSlot = this.getActionSlot(actionSlotId);
    const card = this.getCurrentPlayerCard(cardId);
    const { currentPlayer: player, executor } = this;
    const {
      resources,
      playerLeader: { globalEffect, signetRing },
    } = player;

    const { isHarvest } = actionSlot;
    const cost = this.getActionSlotCost(actionSlotId, cardId);
    if (cost) {
      resources.pay(cost);
    }
    const { hand, playZone, workerPile } = player;
    hand.move(playZone, card);
    const worker = workerPile.last();
    if (!worker) throw new Error("expected a worker");
    workerPile.move(actionSlot.meaples, worker);
    const faction = agentIconToFactionType(actionSlot.agentIcon);
    const { factionUp, chooseGoToBattle, doubleBaseSpice } =
      AgentEffectCommands;

    let actions: AgentEffectDescription[] = [];
    if (
      cost &&
      cost.getResource(ResourceType.Solaris)?.value &&
      AgentEffectDescription.is(globalEffect, "ruthlessNegotiator")
    ) {
      actions.push(AgentEffectCommands.draw);
    }

    if (faction) {
      actions.push(factionUp(faction));
    }
    const cardDef = cardsDefRepository.getDef(card);
    resources.add({ [ResourceType.Spice]: actionSlot.bonusSpice ?? 0 });
    actionSlot.bonusSpice = 0;

    const isSignet = cardDef.playEffects.some((effect) =>
      AgentEffectDescription.is(effect, "signet")
    );
    const shouldPlayCardEffectsFirst =
      isSignet &&
      player.playerLeader.name === "Prince Rhombur Vernius" &&
      ["Tech negociation", "Dreadnought"].includes(actionSlot.id);
    const cardEffects = isSignet ? [signetRing] : cardDef.playEffects;

    if (shouldPlayCardEffectsFirst) {
      actions = [...actions, ...cardEffects, ...actionSlot.effects];
    } else {
      actions = [...actions, ...actionSlot.effects, ...cardEffects];
    }
    if (isHarvest && cardDef.playEffects.includes(doubleBaseSpice)) {
      actions = [...actions, ...actionSlot.effects];
    }
    if (isHarvest && AgentEffectDescription.is(globalEffect, "spiceAdict")) {
      actions.push(globalEffect);
    }
    if (
      actionSlot.effects.some((effect) =>
        AgentEffectDescription.is(effect, "takeSiege")
      ) &&
      AgentEffectDescription.is(globalEffect, "siegeConnections")
    ) {
      actions.push(globalEffect);
    }
    if (actionSlot.isBattle) {
      actions.push(chooseGoToBattle({ type: "dreadnought" }));
      actions.push(chooseGoToBattle({ type: "troop" }));
    }
    const context = { playerId: player.id, cardId: card.id, actionSlotId };
    executor.addAllInReverse(actions, context);
    if (actionSlot.controller && actionSlot.controlEffect) {
      executor.add(actionSlot.controlEffect, {
        playerId: actionSlot.controller,
        actionSlotId,
      });
      this.infoEvents.push(
        gameEventBuilder("controlBonus")({
          playerId: actionSlot.controller,
          slotId: actionSlot.id,
          effect: actionSlot.controlEffect,
        })
      );
    }
    executor.addInHead(AgentEffectCommands.endPlayerTurn, {
      playerId: player.id,
    });
    executor.executeAll();
  }
  get executor() {
    return EffectExecutionManager(this.effectExecution, this);
  }
  validateRevealAll(playerId: string): Validation {
    return Validation.reduce([
      this.validateNoEffect(),
      this.validateCurrentPlayer(playerId),
      this.validateHasNotRevealed(),
    ]);
  }
  revealAll(playerId: string) {
    Validation.validate(this.validateRevealAll(playerId));
    this.infoEvents.push(
      gameEventBuilder("revealAll")({
        playerId,
      })
    );
    this.turnPhase = TurnPhase.Reveal;
    const { currentPlayer, executor } = this;
    const {
      hand,
      revealZone,
      playerLeader: { globalEffect },
    } = currentPlayer;
    const revealedCards = [...currentPlayer.hand.pile];
    revealedCards.forEach((card) => {
      hand.move(revealZone, card);
    });
    currentPlayer.hasRevealed = true;
    revealedCards.forEach((card) => {
      const def = cardsDefRepository.getDef(card);
      executor.addAllInReverse(def.revealEffects, {
        playerId: currentPlayer.id,
        cardId: card.id,
      });
    });
    if (
      AgentEffectDescription.is(
        globalEffect,
        "trashOnRevealWhenPlayingNonFaction"
      )
    ) {
      executor.add(AgentEffectCommands.trashOnRevealWhenPlayingNonFaction, {
        playerId: currentPlayer.id,
      });
    }
    this.addTechEffects(currentPlayer, TurnPhase.Reveal);
    executor.addInHead(AgentEffectCommands.checkEndReveal, {
      playerId: currentPlayer.id,
    });
    executor.executeAll();
  }
  checkEndReveal() {
    const { effectExecution, turnPhase, currentPlayer } = this;
    if (effectExecution.stacks.length === 1 && turnPhase === TurnPhase.Reveal) {
      this.turnPhase = TurnPhase.Shop;
      if (
        this.hasTechTileEffect(currentPlayer, "canBuyOnDeck", TurnPhase.Shop)
      ) {
        currentPlayer.canBuyOnDeck = true;
      }
      this.checkEndBuy();
    }
  }
  checkEndBuy() {
    const { currentPlayer, shopCards, turnPhase } = this;
    if (turnPhase !== TurnPhase.Shop) return;
    if (
      shopCards.every(({ card }) =>
        this.validateBuyCard(currentPlayer.id, card.id)
      )
    ) {
      this.endBuyPhase(currentPlayer.id);
    }
  }
  private validatePhase(phase: TurnPhase): Validation {
    if (this.turnPhase !== phase) return `this is not the ${phase} phase`;
  }
  private validateHasRevealed(): Validation {
    if (!this.currentPlayer.hasRevealed) {
      return "player should reveal his cards first";
    }
  }
  private validateHasNotRevealed(): Validation {
    if (this.currentPlayer.hasRevealed) {
      return "player already revealed his cards";
    }
  }
  validateEndBuyPhase(playerId: string): Validation {
    return Validation.reduce([
      this.validateCurrentPlayer(playerId),
      this.validatePhase(TurnPhase.Shop),
      this.validateHasRevealed(),
    ]);
  }
  startGame() {
    const { players, executor } = this;
    players.forEach((player) => {
      const {
        resources,
        playerLeader: { globalEffect },
      } = player;
      if (AgentEffectDescription.is(globalEffect, "solarisBlessing")) {
        resources.pay({
          [ResourceType.Water]: player.getResource(ResourceType.Water),
        });
      } else if (AgentEffectDescription.is(globalEffect, "intrigueScry")) {
        executor.add(globalEffect, { playerId: player.id });
        executor.executeAll();
      }
    });
    this.startTurn();
  }
  startTurn() {
    const {
      players,
      battleDeck,
      battleDiscard,
      actionSlots,
      currentPlayer,
      executor,
    } = this;
    this.turnPhase = TurnPhase.RoundStart;
    players.forEach((player) => {
      player.draw(5);
      player.bonusCombatPoints = 0;
      player.bonusInfluencePoints = 0;
      player.combatTroops.moveAll(player.orbitTroops);
      const reservedCards = player.reservedShopCards.pile.map(
        ({ card }) => card
      );
      player.trashZone.pushAll(reservedCards);
      player.reservedShopCards = Pile.empty();
      player.TSMFDiscount = 0;
      player.onWin = undefined;
      this.addTechEffects(player, TurnPhase.RoundStart);
      player.techTiles.pile.forEach((tech) => {
        tech.isExhausted = false;
      });
    });
    battleDeck.moveCount(battleDiscard, 1);
    const battleCard = this.currentBattleCard;
    const { controlSlotId } = battleCard.getDef(battleCardsDefRepository);
    const actionSlotOpt = actionSlots.find(({ id }) => id === controlSlotId);
    if (actionSlotOpt && actionSlotOpt.controller) {
      const controllerPlayer = this.getPlayer(actionSlotOpt.controller);
      const { orbitTroops, combatTroops } = controllerPlayer;
      if (orbitTroops.length) {
        this.infoEvents.push(
          gameEventBuilder("freeControlTroop")({
            playerId: controllerPlayer.id,
            slotId: actionSlotOpt.id,
          })
        );
        orbitTroops.moveCount(combatTroops, 1);
      }
    }
    this.startTurnSlotWithDreadnought = actionSlots
      .filter((slot) => slot.controlDreadnought)
      .map((slot) => slot.id);
    actionSlots.forEach((actionSlot) => {
      actionSlot.blockedBy = [];
    });
    currentPlayer.startOfTurnConflict = {
      troop: currentPlayer.combatTroops.length,
      dreadnought: currentPlayer.combatDreadnought.length,
    };
    currentPlayer.startOfTurnGarnison = {
      troop: currentPlayer.garnisonTroops.length,
      dreadnought: currentPlayer.garnisonDreadnought.length,
    };
    executor.executeAll();
    this.turnPhase = TurnPhase.Play;
  }
  validateBuyCard(playerId: string, cardId: string): Validation {
    const { currentPlayer } = this;
    const { influencePoints } = currentPlayer;
    const firstValidation = Validation.reduce([
      this.validateNoEffect(),
      this.validateCurrentPlayer(playerId),
      this.validateHasRevealed(),
      this.validatePhase(TurnPhase.Shop),
    ]);
    if (firstValidation) return firstValidation;
    const {
      cardDef: { cost },
      discount,
    } = this.getShopCardDef(cardId);
    const finalCost = cost - discount;
    if (influencePoints < finalCost) {
      return "not enough influence";
    }
  }
  buyCard(playerId: string, cardId: string) {
    Validation.validate(this.validateBuyCard(playerId, cardId));
    const { executor } = this;
    this.buyCardEffect(playerId, cardId);
    executor.addInHead(AgentEffectCommands.checkEndBuy, { playerId });
    executor.executeAll();
  }
  takeCardFromShop(cardId: string) {
    const { shop, shopDeck } = this;
    const { card, pile } = this.getShopCardDef(cardId);
    if (pile === shop) {
      const temp: Card[] = [];
      shopDeck.moveCount(temp, 1);
      const [newCard] = temp;
      shop.replace(card, newCard);
    } else {
      pile.removeById(card.id);
    }
    return card;
  }
  private buyCardEffect(playerId: string, cardId: string) {
    const player = this.getPlayer(playerId);
    const { cardDef, discount } = this.getShopCardDef(cardId);
    this.acquireCard(playerId, cardId, false);
    const { cost } = cardDef;
    const finalCost = cost - discount;
    player.bonusInfluencePoints -= finalCost;
  }
  acquireCard(playerId: string, cardId: string, onDeck: boolean) {
    const { executor } = this;
    const player = this.getPlayer(playerId);
    const { cardDef } = this.getShopCardDef(cardId);
    const card = this.takeCardFromShop(cardId);
    const { buyEffects = [] } = cardDef;
    const { canBuyOnDeck, discard, deck } = player;
    const dest = onDeck ? deck : discard;
    dest.push(card);
    const effects = [...buyEffects];
    if (canBuyOnDeck) {
      effects.push(AgentEffectCommands.buyOnDeck(card.id));
    }
    executor.addAllInReverse(effects, { playerId });
  }
  reserveCard(playerId: string, cardId: string, discount: number) {
    const card = this.takeCardFromShop(cardId);
    const player = this.getPlayer(playerId);
    player.reservedShopCards.push({ card, discount, id: card.id });
  }
  validatePlayIntrigue(playerId: string, cardId: string): Validation {
    const { turnPhase, actionSlots } = this;
    const validations: Validation[] = [
      this.validateCurrentPlayer(playerId),
      this.validateNoEffect(),
    ];
    if (turnPhase === TurnPhase.CombatEnd && !this.isConflictWinner(playerId)) {
      validations.push("only the conflict winners can play intrigue cards");
    }
    const card = this.getPlayerIntrigueCard(playerId, cardId);
    const cardDef = intrigueCardsDefRepository.getDef(card);
    const playablePhases = cardDef.phaseEffects.map(({ phase }) => phase);
    if (!playablePhases.includes(turnPhase)) {
      validations.push("This card cannot be played during this phase");
    }
    const firstEffect = cardDef.phaseEffects.at(0)?.effects.at(0);
    const player = this.getPlayer(playerId);
    validations.push(
      this.validatePlayableImplication(firstEffect, { game: this, player })
    );
    const { workerPile } = player;
    if (firstEffect) {
      if (
        AgentEffectDescription.is(firstEffect, "recallAgent") &&
        !actionSlots.some((slot) =>
          slot.meaples.pile.some((meaple) => meaple.owner === playerId)
        )
      ) {
        validations.push("no agent to recall");
      } else if (
        AgentEffectDescription.is(firstEffect, "ignoreAgentIcons") &&
        workerPile.isEmpty()
      ) {
        validations.push("no agent to play");
      }
    }
    return Validation.reduce(validations);
  }
  playIntrigue(playerId: string, cardId: string) {
    Validation.validate(this.validatePlayIntrigue(playerId, cardId));
    const { turnPhase, intrigueDiscard, executor, players } = this;
    const player = this.getPlayer(playerId);
    if (turnPhase === TurnPhase.CombatStart) {
      players.forEach((player) => {
        player.passedIntrigue = player.intrigueCards.length ? false : true;
      });
    }
    const card = this.getPlayerIntrigueCard(player.id, cardId);
    const def = intrigueCardsDefRepository.getDef(card);
    player.intrigueCards.move(intrigueDiscard, card);
    const effects: AgentEffectDescription[] = def.phaseEffects
      .filter(({ phase }) => phase === turnPhase)
      .flatMap(({ effects }) => effects);
    if (turnPhase === TurnPhase.CombatStart) {
      executor.addInHead(AgentEffectCommands.toNextFightingPlayer, {
        playerId,
      });
    }
    this.applyEffects(effects, player);
  }
  validatePassIntrigue(playerId: string) {
    const { turnPhase } = this;
    const validations: Validation[] = [
      this.validateCurrentPlayer(playerId),
      this.validateNoEffect(),
    ];
    if (turnPhase === TurnPhase.CombatEnd) {
      const player = this.getPlayer(playerId);
      if (player.passedIntrigue) {
        validations.push("you already passed the intrigue phase");
      }
    } else {
      validations.push(this.validatePhase(TurnPhase.CombatStart));
    }
    return Validation.reduce(validations);
  }
  private canPlayIntrigueCard({
    passedIntrigue,
    intrigueCards,
  }: Player): boolean {
    return Boolean(!passedIntrigue && intrigueCards.length);
  }
  toNextFightingPlayer(startWithCurrent?: true) {
    const { fightingPlayers, conflictWinners, turnPhase } = this;
    const includedPlayers =
      turnPhase === TurnPhase.CombatStart ? fightingPlayers : conflictWinners;
    const playersLeft = includedPlayers.filter((player) =>
      this.canPlayIntrigueCard(player)
    );
    if (!playersLeft.length) {
      switch (turnPhase) {
        case TurnPhase.CombatStart:
          return this.resolveCombat();
        case TurnPhase.CombatEnd:
          return this.resolveWinnersDreadnought();
        default:
          throw new Error(
            "unexpected: toNextFightingPlayer called while not in combat"
          );
      }
    }
    this.toNextPlayerIn(playersLeft, startWithCurrent);
  }
  passIntrigue(playerId: string) {
    Validation.validate(this.validatePassIntrigue(playerId));
    const { turnPhase, conflictWinners } = this;
    const player = this.getPlayer(playerId);
    player.passedIntrigue = true;
    if (turnPhase === TurnPhase.CombatStart) {
      this.toNextFightingPlayer();
    } else if (turnPhase === TurnPhase.CombatEnd) {
      const conflictWinnerThatCanPlayCards = conflictWinners.filter((player) =>
        this.canPlayIntrigueCard(player)
      );
      if (conflictWinnerThatCanPlayCards.length) {
        this.toNextPlayerIn(conflictWinnerThatCanPlayCards);
      } else {
        this.resolveWinnersDreadnought();
      }
    }
  }
  private getPlayerIntrigueCard(playerId: string, cardId: string) {
    const player = this.getPlayer(playerId);
    const card = player.intrigueCards.getById(cardId);
    return card;
  }
  private applyEffects(
    effects: AgentEffectDescription[],
    player: Player,
    card?: Card
  ) {
    const { executor } = this;
    executor.addAllInReverse(effects, {
      playerId: player.id,
      cardId: card?.id,
    });
    executor.executeAll();
  }
  private get shopCards() {
    const {
      shop,
      TSMFCard,
      ArrakisLiaisonCard,
      currentPlayer,
      TSMFPile,
      ArrakisLiaisonPile,
    } = this;
    const { reservedShopCards, TSMFDiscount } = currentPlayer;
    return [
      ...shop.pile.map((card) => ({ card, discount: 0, pile: shop })),
      { card: ArrakisLiaisonCard, discount: 0, pile: ArrakisLiaisonPile },
      { card: TSMFCard, discount: TSMFDiscount, pile: TSMFPile },
      ...reservedShopCards.pile.map(({ card, discount }) => ({
        card,
        discount,
        pile: reservedShopCards,
      })),
    ];
  }
  private getShopCardDef(cardId: string) {
    const result = this.shopCards.find(({ card }) => card.id === cardId);
    if (!result) {
      throw new Error(`could not find shop card with id=${cardId}`);
    }
    const { card } = result;
    const cardDef = cardsDefRepository.getDef(card);
    return { ...result, card, cardDef };
  }
  private getCurrentPlayerCard(cardId: string): Card {
    const {
      currentPlayer: { cards },
    } = this;
    const card = cards.find((card) => card.id === cardId);
    if (!card) {
      throw new Error(`could not find card with id ${cardId}`);
    }
    return card;
  }
  getActionSlot(actionSlotId: string): ActionSlot {
    const actionSlot = this.actionSlots.find(({ id }) => id === actionSlotId);
    if (!actionSlot) {
      throw new Error(`could not find actionSlot with id ${actionSlotId}`);
    }
    return actionSlot;
  }
  endBuyPhase(playerId: string) {
    Validation.validate(this.validateEndBuyPhase(playerId));
    this.infoEvents.push(
      gameEventBuilder("endBuyPhase")({
        playerId,
      })
    );
    const {
      currentPlayer: { playZone, revealZone, discard },
    } = this;
    playZone.moveAll(discard);
    revealZone.moveAll(discard);
    this.endPlayerTurn();
  }
  endPlayerTurn() {
    const { players, currentPlayer } = this;
    currentPlayer.ignoreAgentIcons = false;
    currentPlayer.ignoreOccupiedAgentSlots = false;
    const notRevealedPlayers = players.filter((player) => !player.hasRevealed);
    if (notRevealedPlayers.length) {
      this.toNextPlayerIn(notRevealedPlayers);
      this.turnPhase = TurnPhase.Play;
      const newCurrentPlayer = this.currentPlayer;
      newCurrentPlayer.startOfTurnConflict = {
        troop: newCurrentPlayer.combatTroops.length,
        dreadnought: newCurrentPlayer.combatDreadnought.length,
      };
      newCurrentPlayer.startOfTurnGarnison = {
        troop: newCurrentPlayer.garnisonTroops.length,
        dreadnought: newCurrentPlayer.garnisonDreadnought.length,
      };
      if (
        newCurrentPlayer.intrigueCards.isEmpty() &&
        newCurrentPlayer.workerPile.isEmpty()
      ) {
        this.revealAll(newCurrentPlayer.id);
      }
    } else {
      this.startCombatPhase();
    }
  }
  findCard(cardId: string): Card | undefined {
    const { players, shop, shopDeck, ArrakisLiaisonCard, TSMFCard } = this;
    const cardOpt = players
      .flatMap((player) => player.cards)
      .concat([shop, shopDeck].flatMap((zone) => zone.pile))
      .concat([ArrakisLiaisonCard, TSMFCard])
      .find((card) => card.id === cardId);
    return cardOpt;
  }
  private startCombatPhase() {
    const { fightingPlayers, firstPlayerIndex } = this;
    this.turnPhase = TurnPhase.CombatStart;
    this.currentPlayerIndex = firstPlayerIndex;
    if (fightingPlayers.length) {
      this.players.forEach((player) => {
        player.passedIntrigue = player.intrigueCards.length ? false : true;
      });
      this.toNextFightingPlayer(true);
    } else {
      this.endRound();
    }
  }
  endRound() {
    const {
      firstPlayerIndex,
      players,
      actionSlots,
      battleDeck,
      startTurnSlotWithDreadnought,
    } = this;
    this.turnPhase = TurnPhase.Cleanup;
    if (
      players.some(({ totalVictoryPoints }) => totalVictoryPoints >= 10) ||
      !battleDeck.length
    ) {
      return this.resolveEndGame();
    }
    startTurnSlotWithDreadnought.forEach((slotId) => {
      const slot = this.getActionSlot(slotId);
      const { controlDreadnought } = slot;
      if (!controlDreadnought) {
        throw new Error("unexpected: was expecting a dreadnought");
      }
      const owner = this.getPlayer(controlDreadnought.owner);
      owner.garnisonDreadnought.push(controlDreadnought);
      slot.controlDreadnought = undefined;
    });
    this.turnPhase = TurnPhase.Play;
    this.firstPlayerIndex = (firstPlayerIndex + 1) % players.length;
    this.currentPlayerIndex = this.firstPlayerIndex;
    players.forEach((player) => {
      player.hasRevealed = false;
      player.bonusCombatPoints = 0;
      player.bonusInfluencePoints = 0;
      player.canBuyOnDeck = false;
      player.combatDreadnought.moveAll(player.garnisonDreadnought);
    });
    actionSlots.forEach((actionSlot) => {
      const { meaples, isHarvest, bonusSpice = 0 } = actionSlot;
      meaples.pile.forEach((meaple) => {
        const { owner } = meaple;
        if (owner) {
          const player = this.getPlayer(owner);
          player.workerPile.push(meaple);
        }
      });
      if (!meaples.length && isHarvest) {
        actionSlot.bonusSpice = bonusSpice + 1;
      }
    });
    this.cleanMentat();
    actionSlots.forEach((actionSlot) => {
      actionSlot.meaples = Pile.empty();
    });
    this.startTurn();
  }
  private resolveEndGame() {
    const { players, executor } = this;
    this.turnPhase = TurnPhase.EndGame;
    players.forEach((player) => {
      const { intrigueCards } = player;
      intrigueCards.pile.forEach((card) => {
        const cardDef = card.getDef(intrigueCardsDefRepository);
        cardDef.phaseEffects.forEach(({ phase, effects }) => {
          if (phase === TurnPhase.EndGame) {
            executor.addAllInReverse(effects, { playerId: player.id });
          }
        });
      });
      this.addTechEffects(player, TurnPhase.EndGame);
    });
    executor.executeAll();
  }
  get combatPodium(): Podium<Player> | undefined {
    const { fightingPlayers, players } = this;
    if (!fightingPlayers.length) {
      return;
    }

    return Podium.fromPlayers(
      players.map((player) => ({
        strength: fightingPlayers.includes(player) ? player.combatPoints : 0,
        player,
      }))
    );
  }
  private resolveCombat() {
    const {
      combatPodium,
      players,
      conflictWinners,
      executor,
      currentBattleCard,
    } = this;
    this.turnPhase = TurnPhase.CombatEnd;
    if (!combatPodium || !conflictWinners.length) return;
    const battleCardDef = currentBattleCard.getDef(battleCardsDefRepository);
    combatPodium.forEach(({ group, index }) => {
      const effects = battleCardDef.effects[index - 1];
      group.forEach((player) => {
        executor.addAllInReverse(effects, { playerId: player.id });
      });
    });
    conflictWinners.forEach((player) => {
      const { onWin } = player;
      if (onWin) {
        executor.add(onWin, { playerId: player.id });
      }
      this.addTechEffects(player, TurnPhase.CombatEnd);
    });
    executor.executeAll();
    this.infoEvents.push(gameEventBuilder("conflictWon")(undefined));

    players.forEach((player) => {
      player.passedIntrigue = false;
    });
    const conflictWinnerThatCanPlayCards = conflictWinners.filter((player) =>
      this.canPlayIntrigueCard(player)
    );
    if (conflictWinnerThatCanPlayCards.length) {
      this.toNextPlayerIn(conflictWinnerThatCanPlayCards);
    } else {
      executor.addInHead(AgentEffectCommands.resolveWinnerDreadnought, {
        playerId: this.currentPlayer.id,
      });
    }
    executor.executeAll();
  }
  resolveWinnersDreadnought() {
    const { conflictWinners, executor, currentPlayer } = this;
    if (conflictWinners.some((player) => player.combatDreadnought.length)) {
      conflictWinners.forEach((winner) => {
        executor.add(AgentEffectCommands.dreadnoughtControlSlot, {
          playerId: winner.id,
        });
      });
      executor.addInHead(AgentEffectCommands.endRound, {
        playerId: currentPlayer.id,
      });
      executor.executeAll();
    } else {
      this.endRound();
    }
  }
  checkFactionVictoryPoint(faction: FactionType) {
    const { players } = this;
    const playerWithPoint = players.find(
      (player) => player.getFactionAffinity(faction).hasPoint
    );
    const highestPlayer = sortBy(
      players,
      (player) => -player.getFactionAffinity(faction).level
    )[0];
    if (
      playerWithPoint &&
      highestPlayer.getFactionAffinity(faction).level >
        playerWithPoint.getFactionAffinity(faction).level
    ) {
      playerWithPoint.getFactionAffinity(faction).hasPoint = false;
      highestPlayer.getFactionAffinity(faction).hasPoint = true;
    } else if (
      !playerWithPoint &&
      highestPlayer.getFactionAffinity(faction).level >= 4
    ) {
      highestPlayer.getFactionAffinity(faction).hasPoint = true;
    }
  }
  factionLevelChange(faction: FactionType, player: Player, amount: number) {
    const factionAffinity = player.getFactionAffinity(faction);
    const beforeLevel = factionAffinity.level;
    factionAffinity.level += amount;
    factionAffinity.level = Math.max(0, Math.min(6, factionAffinity.level));
    this.checkFactionVictoryPoint(faction);
    if (factionAffinity.level >= 4 && beforeLevel < 4) {
      const effect = FactionRampBonus[faction];
      this.executor.add(effect, { playerId: player.id });
      this.infoEvents.push(
        gameEventBuilder("factionBonus")({
          playerId: player.id,
          effect,
          faction,
        })
      );
    }
  }
  getPlayer(playerId: string): Player {
    const player = this.players.find((player) => player.id === playerId);
    if (!player) {
      throw new Error(`could not find player with id=${playerId}`);
    }
    return player;
  }
  validateAnswerChoice(
    playerId: string,
    choiceResult: EffectChoiceResult
  ): Validation {
    const { nextChoice } = this;
    if (!nextChoice) {
      return "there is no choice to answer";
    }
    const choice = nextChoice.find((choice) => choice.playerId === playerId);
    if (!choice) {
      return `no choice for player ${playerId}`;
    }
    if (Choice.isAnswered(choice)) {
      return "the choice is already fully answered";
    }
    const { possibleChoices, optional } = choice;
    if (choiceResult.skip) {
      if (!optional) {
        return "cannot skip a mandatory choice";
      }
    } else if (
      !possibleChoices.find((choice) => isJsonEqual(choice, choiceResult.value))
    ) {
      console.info("possible choices", possibleChoices);
      return "did not find this choice as a possible choice";
    } else if (
      choice.results.find((choice) => isJsonEqual(choice, choiceResult))
    ) {
      return "this choice has already been answered";
    }
  }
  answerChoice(playerId: string, choiceResult: EffectChoiceResult) {
    this.executor.answer(choiceResult, playerId);
  }
  private cleanMentat() {
    if (this.skipCleanMentat) {
      this.skipCleanMentat = false;
    } else {
      const mentat = this.retrieveMentatFromAnywhere();
      this.mentatOnSlotOpt = mentat;
    }
  }
  retrieveMentatFromAnywhere(): Meaple {
    const { mentatOnSlotOpt, actionSlots, players } = this;
    if (mentatOnSlotOpt) {
      const mentat = mentatOnSlotOpt;
      this.mentatOnSlotOpt = undefined;
      return mentat;
    }
    const mentatOnSlotResult = findAndMap(actionSlots, ({ meaples }) =>
      meaples.pile.find(Meaple.isMentat)
    );
    if (mentatOnSlotResult) {
      const { item: actionSlot, found: mentat } = mentatOnSlotResult;
      actionSlot.meaples.remove(mentat);
      return mentat;
    }
    const mentatAtPlayerResult = findAndMap(players, (player) =>
      player.workerPile.pile.find(Meaple.isMentat)
    );
    if (mentatAtPlayerResult) {
      const { found: mentat, item: player } = mentatAtPlayerResult;
      player.workerPile.remove(mentat);
      return mentat;
    }
    throw new Error("unexpected: could not find mentat");
  }
  getNextPlayerIn(
    playersGroup: Player[],
    currentPlayerId: string,
    startWithCurrentPlayer: boolean
  ): Player | undefined {
    if (!playersGroup.length) return undefined;
    const { players } = this;
    const sortedPlayers = sortBy(playersGroup, (player) =>
      players.indexOf(player)
    );
    const currentPlayerIndex = players.findIndex(
      (player) => player.id === currentPlayerId
    );
    if (currentPlayerIndex === -1) {
      throw new Error("unexpected: currentPlayerIndex = -1");
    }
    const nextPlayer =
      sortedPlayers.find((player) => {
        const index = players.indexOf(player);
        return index >= currentPlayerIndex + (startWithCurrentPlayer ? 0 : 1);
      }) ?? playersGroup[0];
    return nextPlayer;
  }
  private toNextPlayerIn(
    playersGroup: Player[],
    startWithCurrentPlayer: boolean = false
  ) {
    const { players, currentPlayer } = this;
    const nextPlayer = this.getNextPlayerIn(
      playersGroup,
      currentPlayer.id,
      startWithCurrentPlayer
    );
    if (!nextPlayer) {
      throw new Error("unexpected: nextPlayer undefined");
    }
    this.currentPlayerIndex = players.indexOf(nextPlayer);
  }
  isConflictWinner(playerId: string): boolean {
    return this.conflictWinners.map(({ id }) => id).includes(playerId);
  }
  get conflictWinners(): Player[] {
    const { combatPodium } = this;
    if (!combatPodium) return [];
    const [firstPlace] = combatPodium;
    return firstPlace.group;
  }
  get nextChoice() {
    return this.executor.getCurrentExec()?.blockResult;
  }
  get gamePodium(): Player[] {
    const { players } = this;
    return sortByMultiple(players, [
      (player) =>
        player.totalVictoryPoints +
        (this.hasTechTileEffect(player, "winTiebreakers", TurnPhase.EndGame)
          ? 0.5
          : 0),
      (player) => player.getResource(ResourceType.Spice),
      (player) => player.getResource(ResourceType.Solaris),
      (player) => player.getResource(ResourceType.Water),
      (player) => player.garnisonTroops.length,
    ]).reverse();
  }
  get ArrakisLiaisonCard(): Card {
    const card = this.ArrakisLiaisonPile.last();
    if (!card) {
      throw new Error("unexpectzed");
    }
    return card;
  }
  get TSMFCard(): Card {
    const card = this.TSMFPile.last();
    if (!card) {
      throw new Error("unexpectzed");
    }
    return card;
  }
  validatePlayGlobalEffect(playerId: string): Validation {
    const player = this.getPlayer(playerId);
    const {
      hasPlayedGlobalEffect,
      playerLeader: { globalEffect },
    } = player;
    const isPlayable = AgentEffectDescription.is(globalEffect, "masterstroke");
    return Validation.reduce([
      this.validateCurrentPlayer(playerId),
      this.validateNoEffect(),
      isPlayable ? undefined : "this power is not playable",
      hasPlayedGlobalEffect
        ? "this power has already been used this game"
        : undefined,
    ]);
  }
  playGlobalEffect(playerId: string) {
    Validation.validate(this.validatePlayGlobalEffect(playerId));
    const player = this.getPlayer(playerId);
    const {
      playerLeader: { globalEffect },
    } = player;
    player.hasPlayedGlobalEffect = true;
    this.applyEffects([globalEffect], player);
  }
  getIntrigueCard(cardId: string) {
    const { players, intrigueDiscard, intrigueDeck } = this;
    const cardOpt = [
      ...players.flatMap((player) => player.intrigueCards.pile),
      ...intrigueDiscard.pile,
      ...intrigueDeck.pile,
    ].find((card) => card.id === cardId);
    if (!cardOpt) {
      throw new Error(`could not find intrigue card with id ${cardId}`);
    }
    return cardOpt;
  }
  validateNoEffect(): Validation {
    const automaticEffects: (keyof typeof AgentEffectFunctions)[] = [
      "checkEndBuy",
      "checkEndReveal",
      "endPlayerTurn",
      "toNextFightingPlayer",
      "resolveWinnerDreadnought",
    ];

    const { effectExecution } = this;
    const nonAutoEffects = effectExecution.stacks
      .flatMap((_) => _)
      .filter(({ effect }) =>
        automaticEffects.every(
          (effectName) => !AgentEffectDescription.is(effect, effectName)
        )
      );
    if (nonAutoEffects.length) {
      return "there should not be any effect";
    }
  }
  get turn(): number {
    return this.battleDiscard.length;
  }
  getTechTile(techTileId: string, everywhere = false): TechTile {
    const { techTiles, players, trashedTechTiles } = this;
    let { found: techTileOpt } =
      findAndMap(techTiles, (pile) => pile.findById(techTileId)) ?? {};
    if (!techTileOpt) {
      if (everywhere) {
        const { found } =
          findAndMap(players, (player) =>
            player.techTiles.findById(techTileId)
          ) ?? {};
        techTileOpt = found;
        if (!techTileOpt) {
          techTileOpt = trashedTechTiles.findById(techTileId);
        }
      }
      if (!techTileOpt) {
        throw new Error(`could not find tech tile with id ${techTileId}`);
      }
    }
    return techTileOpt;
  }
  hasTechTileEffect(
    player: Player,
    effectName: keyof typeof AgentEffectFunctions,
    turnPhase: TurnPhase
  ): boolean {
    const { techTiles } = player;
    return techTiles.pile.some((techTile) =>
      techTile.effects.some(
        ({ effects, phase }) =>
          phase === turnPhase &&
          effects.some((effect) =>
            AgentEffectDescription.is(effect, effectName)
          )
      )
    );
  }
  forceRetreatNUnit(playerId: string, unitCount: number) {
    const player = this.getPlayer(playerId);
    const {
      combatDreadnought,
      combatTroops,
      garnisonDreadnought,
      garnisonTroops,
    } = player;
    const retreatedDreadnought = Math.min(combatDreadnought.length, unitCount);
    combatDreadnought.moveCount(garnisonDreadnought, retreatedDreadnought);
    const unitLeft = unitCount - retreatedDreadnought;
    const retreatedTroops = Math.min(combatTroops.length, unitLeft);
    combatTroops.moveCount(garnisonTroops, retreatedTroops);
  }
  validateUseTech(playerId: string, techTileId: string): Validation {
    const { turnPhase } = this;
    const player = this.getPlayer(playerId);
    const tech = player.techTiles.findById(techTileId);
    if (!tech) {
      return "no tech with this id";
    }
    if (tech.isExhausted) {
      return "tech already played this turn";
    }
    const phaseEffect = tech.effects.find(
      ({ phase, forceNotPlayable }) => turnPhase === phase && !forceNotPlayable
    );
    if (!phaseEffect) {
      return `tech has no effect on ${turnPhase} turn phase`;
    }
    const { effects } = phaseEffect;
    const firstEffect = effects.at(0);
    return Validation.reduce([
      this.validateNoEffect(),
      this.validateCurrentPlayer(playerId),
      this.validatePhase(TurnPhase.Play),
      this.validatePlayableImplication(firstEffect, {
        game: this,
        player,
        techTile: tech,
      }),
    ]);
  }
  useTech(playerId: string, techTileId: string) {
    Validation.validate(this.validateUseTech(playerId, techTileId));
    const { executor, turnPhase } = this;
    const player = this.getPlayer(playerId);
    const tech = player.techTiles.getById(techTileId);
    tech.isExhausted = true;
    const effects = tech.effects
      .filter(
        ({ phase, forceNotPlayable }) =>
          phase === turnPhase && !forceNotPlayable
      )
      .flatMap(({ effects }) => effects);
    executor.addAllInReverse(effects, {
      playerId: player.id,
      techId: techTileId,
    });
    executor.executeAll();
  }
  validatePlayableImplication(
    effect: AgentEffectDescription | undefined,
    args: AgentEffectArgs
  ): Validation {
    const drawEffectNames: (keyof typeof AgentEffectFunctions)[] = [
      "draw",
      "drawOrTrashTopCard",
    ];
    if (!effect) {
      return undefined;
    }
    const { player } = args;
    if (
      Proposition.isValueProposition(effect) &&
      drawEffectNames.includes(effect.methodName) &&
      player.deck.isEmpty() &&
      player.discard.isEmpty()
    ) {
      return "No card to draw";
    }
    if (
      Proposition.isImplicationProposition(effect) &&
      Proposition.isValueProposition(effect.cause)
    ) {
      const { cause } = effect;
      if (cause.methodName === "payResource") {
        const cost = getEffectArg<typeof cause.methodName>(cause);
        if (
          !player.resources.canPay({
            [cost.type]: cost.amount,
          })
        ) {
          return "Not enough resources";
        }
      }
      // TODO fix this
      // const result = Command.call(AgentEffectFunctions, effect.cause).apply(
      //   args
      // );
      // if (AgentEffectResult.isBoolean(result) && !result) {
      //   return "The cause effect cannot be played";
      // }
    }
    return undefined;
  }
  addTechEffects(player: Player, phase: TurnPhase) {
    const { techTiles } = player;
    techTiles.pile.forEach((tech) => {
      const { effects } = tech;
      effects
        .filter((effect) => effect.phase === phase)
        .forEach(({ effects }) => {
          this.executor.addAllInReverse(effects, {
            playerId: player.id,
            techId: tech.id,
          });
        });
    });
  }
}
